import { SETTING_RULES, validateSetting } from "../../api/settingsRules";
import { settingDefaultText, type McpSettingParam } from "../../api/mcp";
import { Badge, Panel } from "../ui";
import { Input, Select, Textarea } from "../../f7";
import { SETTING_COPY, settingEffectText } from "./settingsCopy";

/** The anchor a link to one setting uses (the Paused tools panel links to its message). */
export function settingAnchor(key: string): string {
  return `mcp-setting-${key.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
}

/**
 * One settings row: the current value, an input when it is writable, and the
 * rule that governs it.
 *
 * Extracted from Settings.tsx unchanged. That page reads three sources
 * (parameters, staged edits, the apply result) and was long enough that the
 * row's own validation was hard to find inside it.
 *
 * A read-only parameter renders INERT rather than absent — the same argument
 * as Gate: an operator who cannot see a key cannot ask why it is not editable,
 * and `effect` is the answer to that question.
 */
export function EditableSetting({
  param,
  value,
  dirty,
  canWrite,
  onChange,
}: {
  param: McpSettingParam;
  value: string;
  dirty: boolean;
  canWrite: boolean;
  onChange: (v: string) => void;
}) {
  const rule = SETTING_RULES[param.key];
  const error = dirty ? validateSetting(param.key, value) : null;
  const isTemplate = rule?.kind === "template";
  // Copy keyed by SSM path. An unknown key keeps its raw path as the title
  // and its raw `effect` as the timing: shown less helpfully, never hidden.
  const copy = SETTING_COPY[param.key];
  const timing = settingEffectText(param.effect);
  const fallback = settingDefaultText(param);
  const isMessage = isTemplate || param.key.endsWith("_message");

  return (
    // A staged edit is marked by lifting the panel's own ring to the brand
    // colour. `border-accent` did nothing: Panel draws a ring, not a border, so
    // an edited row looked identical to an untouched one.
    <div id={settingAnchor(param.key)} data-setting={param.key} className="scroll-mt-4">
    <Panel className={`p-4 ${dirty ? "ring-brand-500/40 dark:ring-brand-300/40" : ""}`}>
      <div className="mb-2">
        {copy ? (
          <p className="text-sm font-medium text-slate-950 dark:text-white">{copy.label}</p>
        ) : (
          <p className="ref text-sm">{param.key}</p>
        )}
        {copy && (
          <p className="mt-0.5 text-sm/5 text-slate-500 dark:text-slate-400">{copy.description}</p>
        )}
        {timing && <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">{timing}</p>}
        {copy && (
          <p className="ref mt-0.5 font-mono text-xs text-slate-400 dark:text-slate-500">{param.key}</p>
        )}
      </div>

      {/* Unset is not empty. An empty box reads as "merchants see nothing";
          they see MCP's built-in text, which `default` carries (MCP #337).
          Without a default, the sentence alone is still true. */}
      {!param.exists && (
        <div className="mb-1.5 text-xs text-slate-500 dark:text-slate-400">
          <p>
            {isMessage
              ? "Not set: merchants see this built-in text"
              : "Not set: the built-in value applies"}
          </p>
          {fallback !== null && (
            <p className="mt-1 whitespace-pre-wrap break-words rounded bg-slate-100 p-2 font-mono text-slate-400 dark:bg-slate-800/60 dark:text-slate-500">
              {fallback}
            </p>
          )}
        </div>
      )}

      {rule?.kind === "boolean" ? (
        <Select value={value} disabled={!canWrite} onChange={(e) => onChange(e.target.value)}>
          <option value="true">true</option>
          <option value="false">false</option>
        </Select>
      ) : isTemplate ? (
        <Textarea
          value={value}
          disabled={!canWrite}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="font-mono"
          aria-label={param.key}
        />
      ) : (
        // Width on a wrapper — F7's Input is a `w-full` span, so a width class
        // on the Input itself never applied and every field spanned the panel.
        //
        // Narrow for a NUMBER, full width otherwise. A key this page has no
        // rule for could hold anything, and a rate limit and an arbitrary
        // string want opposite widths — sizing both to the number's box put a
        // sentence in a 160px field with no way to see it.
        <div className={rule?.kind === "number" ? "w-40" : "w-full"}>
          <Input
            value={value}
            disabled={!canWrite}
            onChange={(e) => onChange(e.target.value)}
            inputMode={rule?.kind === "number" ? "numeric" : "text"}
            className="font-mono"
            aria-label={param.key}
          />
        </div>
      )}

      <div className="mt-1.5 space-y-1 text-xs">
        {rule?.kind === "number" && rule.min !== undefined && (
          <p className="text-slate-500 dark:text-slate-400">
            Between {rule.min.toLocaleString()} and {rule.max?.toLocaleString()}.
          </p>
        )}
        {isTemplate && (rule?.required?.length ?? 0) > 0 && (
          <p className="text-slate-500 dark:text-slate-400">
            Must contain {rule!.required!.map((p) => `{${p}}`).join(", ")} — the server
            substitutes {rule!.required!.length === 1 ? "it" : "them"} at runtime.
          </p>
        )}
        {/* Inline, not a tooltip: a bounded number field with no context
            invites someone to max it. */}
        {rule?.hazard && <p className="text-amber-700 dark:text-amber-400">{rule.hazard}</p>}
        {error && <p className="text-red-600 dark:text-red-400">{error}</p>}
        {dirty && !error && <Badge tone="amber">changed</Badge>}
      </div>
    </Panel>
    </div>
  );
}
